import { createContext, type Context, useContext } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import type { FieldCategory } from '@atlassian/jira-common-rest/src/internal/simplified/1.0/projects/id/issuetypes/id/types.tsx';
import type { Group } from '../../constants.tsx';
import type { InactiveLayoutItem } from '../../types/layout-items.tsx';

/**
 * Action to associate a layout item with a category and add it to the issue layout.
 *
 * This takes an available, but un-associated item (InactiveLayoutItem)
 * and turns it into an associated ActiveLayoutItem.
 */
export type LayoutItemAddedAction = (
	/**
	 * InactiveLayoutItem is one that has not been added yet - and so we are adding it now!
	 * After this action is done it will become an ActiveLayoutItem.
	 *
	 * If you wish to add an ActiveLayoutItem,
	 * you should prefer to use the reorderLayoutItem action instead:
	 * that one allows reordering, and swapping between field categories too.
	 */
	layoutItem: InactiveLayoutItem,
	/**
	 * Categories are dynamic and configurable: JSW CMP will have different categories from JSM CMP,
	 * which is different from JSW TMP, and so on.
	 * On top of that, some categories have a list of allowed list items types,
	 * and list of allowed field types,
	 * and some field types and layout types have a list of allowed categories!
	 * Also all of that must be memoized to avoid unnecessary re-renders.
	 *
	 * For some examples check out https://hello.atlassian.net/wiki/spaces/JPCE/pages/375800403/Field+categories+configuration
	 * and https://hello.atlassian.net/wiki/spaces/JPCE/pages/405629417/SWIFT+categories+mapping+gallery
	 *
	 * To navigate the jungle I recommend to check out the getFirstPossibleCategory function available in Product Features.
	 */
	category: FieldCategory,
	/**
	 * Zero-indexed position within the target category.
	 * This does not consider the order of categories themselves - so that adding to second category starts from 0 too,
	 * regardless of how many items are in the first category.
	 *
	 * Depending on the configuration, there may or may not be some unsortable layout items,
	 * and unsortable layout items are always on the top.
	 * Therefore the final resulting position may not be the same as the index you provided.
	 */
	index: number,
	/**
	 * Customers can choose to configure their screens to include Tabs.
	 * Tabs config is not available in SWIFT directly, admins have to use global settings: https://support.atlassian.com/jira-service-management-cloud/docs/add-edit-or-remove-field-tabs-from-an-issue-type/
	 *
	 * SWIFT will not attempt to guess which tab to add the layout item to, so you must provide the tabContainerId.
	 * If a tabContainerId is undefined, and category has tabs, the layout item will not be added!
	 *
	 * If a category has no tabs, then the targetTabContainerId should be undefined.
	 */
	targetTabContainerId?: string,
	/**
	 * If true, then after adding the layout item, SWIFT will do two things:
	 * 1. scroll to where the layout item is on the screen, and
	 * 2. render a highlight around the layout item, like a glowing border, to draw attention to what happened
	 *
	 * If false, none of this will happen.
	 * Defaults to false
	 */
	shouldFocusMarkedLayoutItem?: boolean,
	/**
	 * This will become an attribute named `fieldAddedFromGroupId` in the analytics event.
	 * Useful for tracking how many people use which group.
	 */
	itemAddedFromGroupId?: Group,
	/**
	 * This will be sent to analytics; how to exactly find this depends on what event you pass on.
	 * If it's a user button click then you can pass the same event coming from Atlaskit Button for example.
	 *
	 * For general analytics info check out http://go/jira-analytics
	 *
	 * If the analytics event is falsy, then there will be an error logged.
	 *
	 * This really shouldn't be optional at all but typescript demands that a required parameter must not follow optional
	 * so we're stuck with this until we find a volunteer to FF this change.
	 */
	analyticsEvent?: UIAnalyticsEvent,
) => void;

type InputField = 'name' | 'description' | 'helptext';
export type SwiftLifecycleState = {
	issueTypeResetCount: number;
	openCardKeys: string[];
	getConfirmedInputValue: (input: InputField) => string;
	inlineChange: (input: InputField) => (arg1: string) => void;
	cardToggled: (key: string) => void;
	forceValidationFor: string | null;
	setProductModalOpen: (isOpen: boolean) => void;
	addLayoutItem: LayoutItemAddedAction;
};

const initialState: SwiftLifecycleState = {
	issueTypeResetCount: 0,
	openCardKeys: [],
	getConfirmedInputValue: () => '',
	// Replace with lodash/noop
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	inlineChange: () => () => {},
	// Replace with lodash/noop
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	cardToggled: () => {},
	forceValidationFor: null,
	// Replace with lodash/noop
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	setProductModalOpen: () => {},
	// Replace with lodash/noop
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	addLayoutItem: () => {},
};

const SwiftLifecycleContext: Context<SwiftLifecycleState> =
	createContext<SwiftLifecycleState>(initialState);

export const SwiftLifecycleProvider = SwiftLifecycleContext.Provider;

export const useSwiftLifecycle = (): SwiftLifecycleState => useContext(SwiftLifecycleContext);
